import { collection, doc, getDocs, addDoc, query, where, updateDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { COLLECTIONS } from "../collections";
import { toISODate } from "../../utils/date";

/**
 * Atualiza o array de atividades exibidas no documento do membro.
 * Busca todas as matrículas ativas e salva a lista única de nomes de atividade
 * no campo `activityNames` do membro. Falha silenciosa — não interrompe operação principal.
 */
export const updateMemberActivities = async (idTenant, idMember) => {
    try {
        const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
        const q = query(
            enrollmentsRef,
            where("idMember", "==", idMember),
            where("type", "==", "regular")
        );
        const snapshot = await getDocs(q);

        const activityNamesSet = new Set();
        const historicalActivityNamesSet = new Set();
        const classIdsSet = new Set();

        snapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            const isActiveOrSuspended = data.status === "active" || data.status === "suspended";

            if (data.activityName) {
                historicalActivityNamesSet.add(data.activityName);
                if (isActiveOrSuspended) {
                    activityNamesSet.add(data.activityName);
                }
            }
            if (data.idClass && isActiveOrSuspended) {
                classIdsSet.add(data.idClass);
            }
        });

        const activityNames = Array.from(activityNamesSet);
        const historicalActivityNames = Array.from(historicalActivityNamesSet);
        const enrolledClassIds = Array.from(classIdsSet);

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
        await updateDoc(memberRef, {
            activityNames,
            historicalActivityNames,
            enrolledClassIds,
            updatedAt: serverTimestamp()
        });

        return activityNames;
    } catch (error) {
        console.error("[EnrollmentService] Erro ao atualizar activityNames do membro:", error);
        return [];
    }
};

/**
 * Verifica lista de espera após cancelamento de matrícula regular.
 * Cria uma Task para o consultor do primeiro membro na fila. Fire-and-forget.
 */
export const notifyWaitlistOnVacancy = async (idTenant, user, idClass, enrollment) => {
    try {
        const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, idClass);
        const { getDoc } = await import("firebase/firestore");
        const classSnap = await getDoc(classRef);
        if (!classSnap.exists()) return;

        const classData = classSnap.data();
        const waitlistCount = Number(classData.waitlistCount) || 0;
        if (waitlistCount <= 0) return;

        const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);
        const q = query(
            waitlistRef,
            where("idClass", "==", idClass),
            where("status", "==", "waiting"),
            orderBy("position", "asc")
        );
        const snap = await getDocs(q);
        if (snap.empty) return;

        const firstEntry = snap.docs[0].data();

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, firstEntry.idMember);
        const memberSnap = await getDoc(memberRef);
        const memberData = memberSnap.exists() ? memberSnap.data() : {};

        const consultantId = memberData.consultant?.uid || null;
        const activityName = classData.activityName || enrollment.activityName || "Turma";
        const timeSlot = classData.startTime ? `${classData.startTime} - ${classData.endTime || ''}` : "";

        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 2);
        const dueDateStr = toISODate(dueDate);

        const { taskToFirestore } = await import("../../models/task");
        const { TASK_TYPE, TASK_PRIORITY, TASK_STATUS } = await import("../../constants/tasks");

        const taskData = {
            title: `Vaga disponível — ${activityName} ${timeSlot}`.trim(),
            description: `O aluno "${firstEntry.memberName}" está na lista de espera (posição #${firstEntry.position}). Uma vaga foi liberada na turma!`,
            type: TASK_TYPE.CONTACT,
            priority: TASK_PRIORITY.HIGH,
            status: TASK_STATUS.NEW,
            scheduledDate: dueDateStr,
            scheduledTime: "09:00",
            contactReason: "waitlist_available",
            selectedMember: {
                id: firstEntry.idMember,
                fullName: firstEntry.memberName,
                email: firstEntry.memberEmail,
                phone: firstEntry.memberPhone,
                photoUrl: firstEntry.memberPhotoUrl || firstEntry.memberPhoto,
            },
            selectedEmployees: consultantId ? [{
                id: consultantId,
                fullName: memberData.consultant?.name || "",
                email: memberData.consultant?.email || "",
            }] : [],
        };

        const tenant = { idTenant };
        const preparedTask = taskToFirestore(taskData, tenant, user);
        const tasksRef = collection(db, "tenants", idTenant, COLLECTIONS.TASKS);
        await addDoc(tasksRef, preparedTask);

        console.log(`[EnrollmentService] Task criada para lista de espera: ${activityName} → ${firstEntry.memberName}`);
    } catch (error) {
        console.error("[EnrollmentService] Erro ao criar task de lista de espera:", error);
    }
};
